import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useTranslation } from "react-i18next";
import CONSTANTS from "../utils/constants";

export default function AdminUsersAdd() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    password: "",
    role: "administrator",
    permissions: [],
  });

  const [message, setMessage] = useState({ type: "", text: "" });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const pages = [
    { id: 1, text: t("permissions.analytics"), page_id: 1 },
    {
      id: 2,
      text: t("permissions.products"),
      page_id: 2,
      add_id: 3,
      edit_id: 4,
      trash_id: 5,
    },
    { id: 3, text: t("permissions.orders"), page_id: 6 },
    {
      id: 4,
      text: t("permissions.brands"),
      page_id: 7,
      add_id: 8,
      edit_id: 9,
      trash_id: 10,
    },
    {
      id: 5,
      text: t("permissions.categories"),
      page_id: 11,
      add_id: 12,
      edit_id: 13,
      trash_id: 14,
    },
    {
      id: 6,
      text: t("permissions.banners"),
      page_id: 15,
      add_id: 16,
      edit_id: 17,
      trash_id: 18,
    },
    {
      id: 8,
      text: t("permissions.locationPrices"),
      page_id: 23,
      add_id: 24,
      edit_id: 25,
      trash_id: 26,
    },
    {
      id: 9,
      text: t("permissions.orderTimes"),
      page_id: 27,
      add_id: 28,
      edit_id: 29,
      trash_id: 30,
    },
    
    {
      id: 10,
      text: t("permissions.users"),
      page_id: 31,
      add_id: 32,
      edit_id: 33,
      trash_id: 34,
    },
    { id: 11, text: t("permissions.settings"), page_id: 35 },
  ];

  const allPermissionIds = pages.flatMap((page) => {
    const ids = [];
    if (page.page_id) ids.push(page.page_id);
    if (page.add_id) ids.push(page.add_id);
    if (page.edit_id) ids.push(page.edit_id);
    if (page.trash_id) ids.push(page.trash_id);
    return ids;
  });

  const isAllSelected =
    formData.permissions.length === allPermissionIds.length &&
    allPermissionIds.every((id) => formData.permissions.includes(id));

  const handleSelectAll = () => {
    const newPermissions = isAllSelected ? [] : [...allPermissionIds];
    setFormData((prev) => ({ ...prev, permissions: newPermissions }));
  };

  const handleInputChange = (e) => {
    const { id, value } = e.target;
    setFormData((prev) => ({ ...prev, [id]: value }));
  };

  const handleRoleChange = (e) => {
    setFormData((prev) => ({ ...prev, role: e.target.value }));
  };

  const handlePermissionChange = (permissionId) => {
    setFormData((prev) => {
      const permissions = prev.permissions.includes(permissionId)
        ? prev.permissions.filter((id) => id !== permissionId)
        : [...prev.permissions, permissionId];
      return { ...prev, permissions };
    });
  };

  const showNotification = (type, text, duration = 4000) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: "", text: "" }), duration);
  };

  const handleSubmit = async (e) => {
  e.preventDefault();

  if (!formData.name.trim() || !formData.phone.trim() || !formData.password) {
    showNotification("danger", t("users.add.formRequired"));
    return;
  }

  setLoading(true);
  setMessage({ type: "", text: "" });

  try {
    await axios.post(`${CONSTANTS.baseRoute}/users`, {
      ...formData,
      phone: formData.phone.trim(), 
    });

    showNotification("success", t("users.add.successMessage"));
    setFormData({
      name: "",
      phone: "",
      password: "",
      role: "administrator",
      permissions: [],
    });

    setTimeout(() => navigate("/admin/home/users"), 1800);
  } catch (error) {
    showNotification(
      "danger",
      error.response?.data?.error || t("users.add.errorMessage")
    );
  } finally {
    setLoading(false);
  }
};

  return (
    <div className="admin-main ps-2 pe-3 py-3">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div className="d-flex gap-2 align-items-center">
          <button className="btn btn-light shadow-sm" onClick={() => navigate(-1)}>
            <i className="bi bi-chevron-left"></i>
          </button>
          <button className="btn btn-light shadow-sm" onClick={() => navigate(+1)}>
            <i className="bi bi-chevron-right"></i>
          </button>
          <h4 className="fw-bold mb-0">{t("users.add.title")}</h4>
        </div>
      </div>

      {/* Сообщение */}
      {message.text && (
        <div
          className={`alert alert-${message.type} shadow-sm mb-4 alert-dismissible fade show`}
          role="alert"
        >
          {message.text}
          <button
            type="button"
            className="btn-close"
            onClick={() => setMessage({ type: "", text: "" })}
          ></button>
        </div>
      )}

      <div className="card shadow-sm border rounded-3">
        <div
          className="card-header text-white fw-bold py-3"
          style={{ background: "linear-gradient(135deg, #F47D39, #e66a1f)" }}
        >
          <i className="bi bi-person-plus me-2"></i>
          {t("users.add.formTitle")}
        </div>

        <div className="card-body p-4">
          <form onSubmit={handleSubmit}>
            <div className="row g-4">
              {/* Имя */}
              <div className="col-md-6">
                <label className="form-label fw-bold">
                  {t("users.name")} <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  className="form-control shadow-sm"
                  placeholder={t("users.add.namePlaceholder")}
                  id="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                />
              </div>

              {/* Телефон */}
              <div className="col-md-6">
                <label className="form-label fw-bold">
                  {t("users.phone")} <span className="text-danger">*</span>
                </label>
                <div className="input-group shadow-sm">
                  <span className="input-group-text">+993</span>
                  <input
                    type="tel"
                    className="form-control"
                    placeholder="61234567"
                    id="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    maxLength={8}
                    pattern="[0-9]{8}"
                    required
                  />
                </div>
              </div>

              {/* Пароль */}
              <div className="col-md-6">
                <label className="form-label fw-bold">
                  {t("users.add.password")} <span className="text-danger">*</span>
                </label>
                <div className="input-group shadow-sm">
                  <input
                    type={showPassword ? "text" : "password"}
                    className="form-control"
                    id="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    required
                    minLength={6}
                  />
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    <i className={`bi ${showPassword ? "bi-eye-slash" : "bi-eye"}`}></i>
                  </button>
                </div>
              </div>

              {/* Роль */}
              <div className="col-md-6">
                <label className="form-label fw-bold">{t("users.role")}</label>
                <select
                  className="form-select shadow-sm"
                  id="role"
                  value={formData.role}
                  onChange={handleRoleChange}
                >
                  <option value="administrator">{t("users.add.roleAdmin")}</option>
                  <option value="manager">{t("users.add.roleManager")}</option>
                  <option value="worker">{t("users.add.roleWorker")}</option>
                </select>
              </div>
            </div>

            {/* Права доступа */}
            <div className="mt-5">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="fw-bold mb-0">{t("users.permissions")}</h6>
                <div className="form-check">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="selectAll"
                    checked={isAllSelected}
                    onChange={handleSelectAll}
                  />
                  <label className="form-check-label" htmlFor="selectAll">
                    {t("common.selectAll")}
                  </label>
                </div>
              </div>

              <div className="table-responsive border rounded shadow-sm">
                <table className="table table-hover mb-0 text-center align-middle">
                  <thead className="table-light">
                    <tr>
                      <th className="text-start ps-3">{t("users.model")}</th>
                      <th>{t("permissions.read")}</th>
                      <th>{t("permissions.create")}</th>
                      <th>{t("permissions.update")}</th>
                      <th>{t("permissions.delete")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pages.map((page) => (
                      <tr key={page.id}>
                        <td className="text-start ps-3 fw-medium">{page.text}</td>
                        <td>
                          {page.page_id && (
                            <input
                              type="checkbox"
                              className="form-check-input"
                              checked={formData.permissions.includes(page.page_id)}
                              onChange={() => handlePermissionChange(page.page_id)}
                            />
                          )}
                        </td>
                        <td>
                          {page.add_id && (
                            <input
                              type="checkbox"
                              className="form-check-input"
                              checked={formData.permissions.includes(page.add_id)}
                              onChange={() => handlePermissionChange(page.add_id)}
                            />
                          )}
                        </td>
                        <td>
                          {page.edit_id && (
                            <input
                              type="checkbox"
                              className="form-check-input"
                              checked={formData.permissions.includes(page.edit_id)}
                              onChange={() => handlePermissionChange(page.edit_id)}
                            />
                          )}
                        </td>
                        <td>
                          {page.trash_id && (
                            <input
                              type="checkbox"
                              className="form-check-input"
                              checked={formData.permissions.includes(page.trash_id)}
                              onChange={() => handlePermissionChange(page.trash_id)}
                            />
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Кнопка отправки */}
            <div className="mt-5 d-flex justify-content-end">
              <button
                type="submit"
                className="btn btn-success px-5 py-2 fw-bold shadow-sm"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2"></span>
                    {t("common.loading")}
                  </>
                ) : (
                  <>
                    <i className="bi bi-person-plus me-2"></i>
                    {t("common.add")}
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}